import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  Chip,
  Badge,
  Button,
  Paper,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  FolderOpen,
  Group,
  Business,
  Timer,
  AccountCircle,
  Logout,
  Settings,
  Brightness4,
  Brightness7,
  Assignment,
  Inbox,
  People,
  TrendingUp,
  LocalShipping,
  Science,
  Notifications,
  Stop,
  PlayArrow,
  Pause,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useTheme } from '../../contexts/ThemeContext';
import { UserRole, Notification, NotificationType } from '../../types';
import { hasPermission, getAccessibleResources } from '../../config/permissions';
import { apiService } from '../../services/api';
import buildInfo from '../../buildInfo.json';
import { useTranslation } from 'react-i18next';
import { usePreloadData } from '../../hooks/usePreloadData';
import { LanguageToggle } from '../LanguageToggle';
import ChangelogDialog from '../Changelog/ChangelogDialog';
import { permissionBroadcaster } from '../../utils/broadcastChannel';
import toast from 'react-hot-toast';

const drawerWidth = 240;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeTimer, setActiveTimer] = useState<any>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [userAvatar, setUserAvatar] = useState<string | null>(null);
  
  // Preload common data to improve perceived performance
  usePreloadData();
  const [changelogOpen, setChangelogOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, clearAuth, refreshCurrentUser } = useAuthStore();
  const { darkMode, toggleDarkMode } = useTheme();
  const { t } = useTranslation();

  useEffect(() => {
    // Refresh user data on mount to ensure permissions are up to date
    refreshCurrentUser();
    
    // Load notifications on mount only
    loadNotifications();
    
    // Check for active timer
    checkActiveTimer();
    // REMOVED: Automatic notification polling to prevent excessive API calls
    // Notifications will refresh when user navigates between pages
  }, []);
  
  // Load avatar when user has one
  useEffect(() => {
    const loadAvatar = async () => {
      if (!user || !user.hasAvatar) {
        setUserAvatar(null);
        return;
      }
      
      // Wait a bit to ensure auth is fully loaded
      const authState = useAuthStore.getState();
      if (!authState.accessToken) {
        console.log('No access token available, waiting...');
        setTimeout(loadAvatar, 100);
        return;
      }
      
      try {
        const response = await apiService.getUserAvatar();
        if (response.data?.avatar) {
          setUserAvatar(response.data.avatar);
        }
      } catch (error) {
        console.error('Failed to load avatar:', error);
        setUserAvatar(null);
      }
    };
    
    loadAvatar();
  }, [user?.id, user?.hasAvatar]);
  
  // REMOVED: Periodic user data refresh to prevent excessive API calls
  // User data will refresh when navigating between pages or on manual refresh
  
  // Listen for permission updates from other tabs or windows
  useEffect(() => {
    permissionBroadcaster.onPermissionUpdate((message) => {
      if (user && user.id === message.userId) {
        // User's permissions were updated, refresh their data
        refreshCurrentUser();
      }
    });
    
    return () => {
      // Cleanup is handled by the singleton
    };
  }, [user]);

  // Update elapsed time for active timer
  useEffect(() => {
    if (!activeTimer) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const start = new Date(activeTimer.startTime).getTime();
      setElapsedTime(Math.floor((now - start) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [activeTimer]);

  const checkActiveTimer = async () => {
    try {
      const response = await apiService.getActiveTimer();
      if (response.data) {
        setActiveTimer(response.data);
        const start = new Date(response.data.startTime).getTime();
        setElapsedTime(Math.floor((Date.now() - start) / 1000));
      }
    } catch (error) {
      // No active timer
      setActiveTimer(null);
      setElapsedTime(0);
    }
  };

  const handleStopTimer = async () => {
    if (!activeTimer) return;

    try {
      await apiService.stopTimer();
      setActiveTimer(null);
      setElapsedTime(0);
      toast.success('Timer stopped');
      // Refresh time tracking page if we're on it
      if (location.pathname === '/time-tracking') {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error stopping timer:', error);
      toast.error('Failed to stop timer');
    }
  };

  const formatElapsedTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const loadNotifications = async () => {
    try {
      const response = await apiService.getNotifications();
      // Handle paginated response
      const notificationsData = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.data || []);
      setNotifications(notificationsData);
      setUnreadCount(notificationsData.filter((n: any) => !n.isRead).length);
    } catch (error: any) {
      // Silently ignore 404 errors as notifications endpoint might not be implemented
      if (error.response?.status !== 404) {
        console.error('Error loading notifications:', error);
      }
      // Set empty notifications to prevent UI issues
      setNotifications([]);
      setUnreadCount(0);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuth();
      handleClose();
    }
  };

  const handleProfile = () => {
    navigate('/profile');
    handleClose();
  };

  const handleNotificationOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const getNotificationText = (notification: Notification, field: 'title' | 'message') => {
    const text = notification[field];
    if (!text) return '';
    
    // Check if it's a translation key (contains dots)
    if (text.includes('.')) {
      try {
        return t(text);
      } catch {
        // If translation fails, format the key nicely
        return text.split('.').pop()?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || text;
      }
    }
    
    return text;
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Only mark as read if it's not already read
    if (!notification.isRead) {
      try {
        // Call API to mark notification as read
        await apiService.markNotificationAsRead(notification.id);
        
        // Update local state
        const updatedNotifications = notifications.map(n => 
          n.id === notification.id ? { ...n, isRead: true } : n
        );
        setNotifications(updatedNotifications);
        setUnreadCount(updatedNotifications.filter(n => !n.isRead).length);
      } catch (error) {
        console.error('Error marking notification as read:', error);
        // Still update UI optimistically even if API call fails
        const updatedNotifications = notifications.map(n => 
          n.id === notification.id ? { ...n, isRead: true } : n
        );
        setNotifications(updatedNotifications);
        setUnreadCount(updatedNotifications.filter(n => !n.isRead).length);
      }
    }
    
    // Navigate based on notification type or actionUrl
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    } else {
      // Navigate based on notification type
      switch (notification.type) {
        case NotificationType.TASK_ASSIGNED:
        case NotificationType.TASK_UPDATED:
        case NotificationType.TASK_COMMENT:
          navigate('/tasks');
          break;
        case NotificationType.PROJECT_INVITE:
          navigate('/projects');
          break;
        case NotificationType.TEAM_INVITE:
          navigate('/teams');
          break;
        case NotificationType.OPPORTUNITY_UPDATED:
          navigate('/crm');
          break;
        case NotificationType.MENTION:
          // If there's specific data about where the mention occurred, navigate there
          if (notification.data?.taskId) {
            navigate('/tasks');
          } else if (notification.data?.projectId) {
            navigate('/projects');
          } else {
            navigate('/dashboard');
          }
          break;
        default:
          navigate('/dashboard');
      }
    }
    
    handleNotificationClose();
  };

  const menuItems = [
    { text: t('navigation.dashboard'), icon: <Dashboard />, path: '/dashboard', resource: 'dashboard' },
    { text: t('navigation.projects'), icon: <FolderOpen />, path: '/projects', resource: 'projects' },
    { text: t('navigation.teams'), icon: <Group />, path: '/teams', resource: 'teams' },
    { text: t('navigation.crm'), icon: <Business />, path: '/crm', resource: 'crm' },
    { text: t('navigation.sales'), icon: <TrendingUp />, path: '/sales', resource: 'sales' },
    { text: t('navigation.sourcing'), icon: <LocalShipping />, path: '/sourcing', resource: 'sourcing' },
    { text: t('navigation.research'), icon: <Science />, path: '/research', resource: 'research' },
    { text: t('navigation.timeTracking'), icon: <Timer />, path: '/time-tracking', resource: 'timeTracking' },
    { text: t('navigation.requests'), icon: <Inbox />, path: '/requests', resource: 'requests' },
    { text: t('navigation.users'), icon: <People />, path: '/users', resource: 'users' },
  ];
  
  // Filter menu items based on permissions
  const visibleMenuItems = menuItems.filter(item => {
    if (!user) return false;
    return hasPermission(user.role, item.resource, 'view', user.customPermissions);
  });
  
  // Hide menu for members unless they have specific permissions
  const showMenu = visibleMenuItems.length > 0;

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2 }}>
        <Box
          component="img"
          src="/logo.png"
          alt="Group Legion Panel"
          sx={{
            height: 28,
            mr: 2,
          }}
        />
        <Typography
          variant="h6"
          noWrap
          component="div"
          sx={{ fontWeight: 700, color: 'primary.main' }}
        >
          Group Legion Panel
        </Typography>
      </Toolbar>
      <Divider />
      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
        <List>
        {visibleMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => navigate(item.path)}
              sx={{
                borderLeft: '3px solid transparent',
                transition: 'all 0.2s ease',
                '&.Mui-selected': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.05)' 
                    : 'rgba(0, 0, 0, 0.05)',
                  borderLeftColor: (theme) => theme.palette.primary.main,
                  color: (theme) => theme.palette.mode === 'dark' 
                    ? '#ffffff' 
                    : theme.palette.primary.main,
                  '& .MuiListItemIcon-root': {
                    color: (theme) => theme.palette.mode === 'dark' 
                      ? '#ffffff' 
                      : theme.palette.primary.main,
                  },
                },
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.08)' 
                    : 'rgba(0, 0, 0, 0.08)',
                  color: (theme) => theme.palette.mode === 'dark' 
                    ? '#ffffff' 
                    : theme.palette.primary.main,
                  borderLeftColor: (theme) => theme.palette.primary.main,
                  '& .MuiListItemIcon-root': {
                    color: (theme) => theme.palette.mode === 'dark' 
                      ? '#ffffff' 
                      : theme.palette.primary.main,
                  },
                },
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      </Box>
      <Divider />
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography 
          variant="caption" 
          color="text.secondary"
          sx={{ 
            cursor: 'pointer',
            '&:hover': {
              color: 'primary.main',
              textDecoration: 'underline'
            }
          }}
          onClick={() => setChangelogOpen(true)}
        >
          v{buildInfo.version}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <LanguageToggle />
          <Tooltip title={darkMode ? t('profile.lightTheme') : t('profile.darkTheme')}>
            <IconButton
              size="small"
              onClick={toggleDarkMode}
              color="inherit"
            >
              {darkMode ? <Brightness7 sx={{ fontSize: 20 }} /> : <Brightness4 sx={{ fontSize: 20 }} />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ 
      display: 'flex',
      '& @keyframes pulse': {
        '0%': { opacity: 1 },
        '50%': { opacity: 0.7 },
        '100%': { opacity: 1 }
      }
    }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: showMenu ? `calc(100% - ${drawerWidth}px)` : '100%' },
          ml: { sm: showMenu ? `${drawerWidth}px` : 0 },
        }}
      >
        <Toolbar>
          {showMenu && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            {visibleMenuItems.find(item => item.path === location.pathname)?.text || 'Group Legion Panel'}
            {user?.role === UserRole.SUPERADMIN && (
              <Chip 
                label="SUPERADMIN" 
                size="small" 
                color="error" 
                sx={{ 
                  height: 20,
                  fontSize: '0.7rem',
                  fontWeight: 'bold',
                  animation: 'pulse 2s infinite'
                }} 
              />
            )}
          </Typography>
          {activeTimer && (
            <Paper
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 0.5,
                mr: 2,
                backgroundColor: 'error.main',
                color: 'error.contrastText',
              }}
              elevation={3}
            >
              <Timer fontSize="small" />
              <Typography variant="body2" fontWeight="bold">
                {formatElapsedTime(elapsedTime)}
              </Typography>
              <Tooltip title="Stop Timer">
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={handleStopTimer}
                  sx={{ 
                    ml: 1,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    }
                  }}
                >
                  <Stop fontSize="small" />
                </IconButton>
              </Tooltip>
            </Paper>
          )}
          <IconButton
            size="large"
            color="inherit"
            onClick={handleNotificationOpen}
            sx={{ mr: 1 }}
          >
            <Badge badgeContent={unreadCount} color="error">
              <Notifications />
            </Badge>
          </IconButton>
          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenu}
            color="inherit"
          >
            <Avatar
              src={userAvatar}
              sx={{ width: 32, height: 32 }}
            >
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </Avatar>
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <MenuItem onClick={handleProfile}>
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              {t('navigation.profile')}
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              {t('navigation.logout')}
            </MenuItem>
          </Menu>
          
          {/* Notifications Menu */}
          <Menu
            anchorEl={notificationAnchor}
            open={Boolean(notificationAnchor)}
            onClose={handleNotificationClose}
            PaperProps={{
              sx: { width: 360, maxHeight: 400 }
            }}
          >
            <Box px={2} py={1}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6">{t('notifications.title')}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {notifications.length} {t('notifications.total', 'total')}
                </Typography>
              </Box>
              {notifications.length > 0 && (
                <Box display="flex" gap={1} flexWrap="wrap">
                  {notifications.some(n => !n.isRead) && (
                    <Button
                      size="small"
                      variant="text"
                      sx={{ fontSize: '0.75rem', py: 0.5 }}
                      onClick={async () => {
                        try {
                          // Mark all notifications as read
                          await Promise.all(
                            notifications
                              .filter(n => !n.isRead)
                              .map(n => apiService.markNotificationAsRead(n.id))
                          );
                          
                          // Update local state
                          setNotifications(notifications.map(n => ({ ...n, isRead: true })));
                          setUnreadCount(0);
                          toast.success(t('notifications.allMarkedAsRead', 'All notifications marked as read'));
                        } catch (error) {
                          console.error('Error marking all as read:', error);
                          // Still update UI optimistically
                          setNotifications(notifications.map(n => ({ ...n, isRead: true })));
                          setUnreadCount(0);
                        }
                      }}
                    >
                      {t('notifications.markAllRead', 'Mark all read')}
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="text"
                    color="error"
                    sx={{ fontSize: '0.75rem', py: 0.5 }}
                    onClick={async () => {
                      if (window.confirm(t('notifications.confirmClearAll', 'Are you sure you want to clear all notifications?'))) {
                        try {
                          // Clear all notifications via API if endpoint exists
                          await apiService.clearAllNotifications?.();
                        } catch (error) {
                          console.error('Error clearing notifications:', error);
                        }
                        
                        // Update local state
                        setNotifications([]);
                        setUnreadCount(0);
                        toast.success(t('notifications.allCleared', 'All notifications cleared'));
                      }
                    }}
                  >
                    {t('notifications.clearAll', 'Clear all')}
                  </Button>
                </Box>
              )}
            </Box>
            <Divider />
            {notifications.length === 0 ? (
              <Box p={2} textAlign="center">
                <Typography variant="body2" color="text.secondary">
                  {t('notifications.noNotifications')}
                </Typography>
              </Box>
            ) : (
              notifications.map((notification) => (
                <MenuItem
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    backgroundColor: !notification.isRead ? 'action.hover' : 'transparent',
                    borderLeft: !notification.isRead ? '4px solid' : 'none',
                    borderLeftColor: 'primary.main',
                    position: 'relative',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemIcon>
                    <Badge
                      color="primary"
                      variant="dot"
                      invisible={notification.isRead}
                      sx={{
                        '& .MuiBadge-badge': {
                          right: 2,
                          top: 2,
                        },
                      }}
                    >
                      {notification.type === NotificationType.TASK_ASSIGNED || 
                       notification.type === NotificationType.TASK_UPDATED || 
                       notification.type === NotificationType.TASK_COMMENT ? (
                        <Assignment />
                      ) : notification.type === NotificationType.PROJECT_INVITE ? (
                        <FolderOpen />
                      ) : notification.type === NotificationType.TEAM_INVITE ? (
                        <Group />
                      ) : notification.type === NotificationType.OPPORTUNITY_UPDATED ? (
                        <Business />
                      ) : notification.type === NotificationType.MENTION ? (
                        <People />
                      ) : (
                        <Inbox />
                      )}
                    </Badge>
                  </ListItemIcon>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography 
                      variant="body2" 
                      fontWeight={!notification.isRead ? 600 : 400}
                      sx={{ 
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {getNotificationText(notification, 'title')}
                    </Typography>
                    {notification.message && (
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ 
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {getNotificationText(notification, 'message')}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      {new Date(notification.createdAt).toLocaleDateString()} {new Date(notification.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </Box>
                </MenuItem>
              ))
            )}
          </Menu>
        </Toolbar>
      </AppBar>
      {showMenu && (
        <Box
          component="nav"
          sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
          aria-label="navigation"
        >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      )}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: showMenu ? `calc(100% - ${drawerWidth}px)` : '100%' },
        }}
      >
        <Toolbar />
        {children}
      </Box>

      {/* Changelog Dialog */}
      <ChangelogDialog
        open={changelogOpen}
        onClose={() => setChangelogOpen(false)}
        version={buildInfo.version}
      />
    </Box>
  );
};

export default Layout;